/**
 * Cell renderers for the two content tables (SPEC `Content Performance` § Posts mode, § Comments
 * mode).
 *
 * Ids, labels, alignment, sortability and default visibility all live in
 * `packages/shared/dto/content.ts`; this module supplies only the markup per cell and the pixel
 * width per column. Every number arrives as an `Explained` and every chip as a `MultiplierDTO`, so
 * `<Num>`/`<MultiplierBadge>` are the only way a figure reaches the screen and a raw number cannot
 * (§12.5 rule 2). Dates arrive pre-spelled (`*Display`) — nothing here formats one.
 *
 * A metric and its ×-median chip are **two columns** over one row field: they sort identically (one
 * dataset-wide median divides both), so splitting them costs nothing and lets the reader hide either
 * half. The `×` id maps back to the row key here, at the edge of the registry — the #88/#89 payload
 * never grew a field for it.
 *
 * The one cell with real behaviour is **Post**: an enriched row (`postId !== null`) is text the
 * scraper already stores and the row click opens the shared `?post=` popup, so the cell stays inert
 * and lets the row handle it, and it ends with the main table's own `PostThumb`; an unenriched row
 * is the export's own placeholder and carries the quiet "not tracked by scraping" dot plus a link to
 * LinkedIn, because there is no local post to open. Links stop their own click (`ContentTable`
 * treats an `<a>` press as not-a-row-activation).
 */

import type { ReactNode } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import {
  CONTENT_COMMENTS_COLUMNS,
  CONTENT_POSTS_COLUMNS,
  type ContentCommentRowDTO,
  type ContentCommentsColumnId,
  type ContentMetricCellDTO,
  type ContentPostRowDTO,
  type ContentPostsColumnId,
} from '@lw/shared';
import { ExternalLinkIcon, MultiplierBadge, Num } from '../../components';
import { PostThumb } from '../view/posts/postsColumns';

/** Hover text of the quiet dot on a row scraping has never seen. */
const UNTRACKED_TITLE = 'not tracked by scraping';
/** Appended to a cell's hover text when LinkedIn's own export flagged the text as cut off. */
const TRUNCATED_NOTE = '(text truncated by LinkedIn’s export)';

/**
 * The ×-median chip on its own. Its tooltip appears only where it must explain itself — an
 * insufficient baseline owes the reader a reason for its "—". The dense grid keeps the rest quiet;
 * the full formula rides the metrics sections.
 */
function MultiplierCell({ cell }: { cell: ContentMetricCellDTO }) {
  return <MultiplierBadge value={cell.multiplier} withTooltip={cell.multiplier.insufficient} />;
}

/** External link that never doubles as a row activation. */
function OutLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      className="cn-link"
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      onClick={(event) => event.stopPropagation()}
    >
      {children}
    </a>
  );
}

/* ========================================================================== */
/*  Posts                                                                     */
/* ========================================================================== */

/** A `×` column is a fixed narrow slot: three digits and the glyph, never a five-figure number. */
const MULTIPLIER_WIDTH = 76;

const POSTS_COLUMN_WIDTH: Partial<Record<ContentPostsColumnId, number>> = {
  post: 0,
  postedAt: 170,
  impressions: 110,
  impressionsX: MULTIPLIER_WIDTH,
  membersReached: 130,
  membersReachedX: MULTIPLIER_WIDTH,
  reactions: 100,
  reactionsX: MULTIPLIER_WIDTH,
  comments: 100,
  commentsX: MULTIPLIER_WIDTH,
  reposts: 100,
  repostsX: MULTIPLIER_WIDTH,
  saves: 90,
  savesX: MULTIPLIER_WIDTH,
  sends: 90,
  sendsX: MULTIPLIER_WIDTH,
  engagements: 120,
  engagementsX: MULTIPLIER_WIDTH,
  profileViewers: 130,
  profileViewersX: MULTIPLIER_WIDTH,
  followersGained: 140,
  followersGainedX: MULTIPLIER_WIDTH,
  inNetworkPct: 130,
  capturedAt: 170,
  profileVisitRate: 150,
  followersRate: 120,
  reactionsRate: 120,
  commentsRate: 120,
  repostsRate: 120,
  savesRate: 120,
  sendsRate: 120,
};

export function contentPostsColumnWidth(id: ContentPostsColumnId): number {
  return POSTS_COLUMN_WIDTH[id] ?? 130;
}

export const CONTENT_POSTS_FLEX_COLUMN: ContentPostsColumnId = 'post';

function PostCell({ row, mediaEnabled }: { row: ContentPostRowDTO; mediaEnabled: boolean }) {
  const title = row.textTruncated ? `${row.previewText} ${TRUNCATED_NOTE}` : row.previewText;
  return (
    <span className="cell-preview">
      {row.enriched ? null : <span className="cn-untracked" title={UNTRACKED_TITLE} />}
      <span className="cell-ellipsis" title={title}>
        {row.previewText}
      </span>
      {/* The permalink is the only way into an unenriched row; an enriched one opens locally. */}
      {row.enriched || row.permalink === null ? null : (
        <OutLink href={row.permalink}>
          <ExternalLinkIcon size={12} />
          <span className="sr-only">Open on LinkedIn</span>
        </OutLink>
      )}
      {/* Only a tracked row has a scraped asset to show; `trackedContentType` is that discriminant. */}
      {row.trackedContentType === null ? null : (
        <PostThumb
          thumb={row.thumb}
          contentType={row.trackedContentType}
          mediaEnabled={mediaEnabled}
        />
      )}
    </span>
  );
}

export type ContentPostsColumnContext = { mediaEnabled: boolean };

function postsCellRenderer(id: ContentPostsColumnId, context: ContentPostsColumnContext) {
  return function Cell({ row }: { row: ContentPostRowDTO }) {
    switch (id) {
      case 'post':
        return <PostCell row={row} mediaEnabled={context.mediaEnabled} />;
      case 'postedAt':
        return <span>{row.postedAtDisplay}</span>;
      case 'impressions':
        return <Num value={row.impressions.value} withTooltip={false} />;
      case 'impressionsX':
        return <MultiplierCell cell={row.impressions} />;
      case 'membersReached':
        return <Num value={row.membersReached.value} withTooltip={false} />;
      case 'membersReachedX':
        return <MultiplierCell cell={row.membersReached} />;
      case 'reactions':
        return <Num value={row.reactions.value} withTooltip={false} />;
      case 'reactionsX':
        return <MultiplierCell cell={row.reactions} />;
      case 'comments':
        return <Num value={row.comments.value} withTooltip={false} />;
      case 'commentsX':
        return <MultiplierCell cell={row.comments} />;
      case 'reposts':
        return <Num value={row.reposts.value} withTooltip={false} />;
      case 'repostsX':
        return <MultiplierCell cell={row.reposts} />;
      case 'saves':
        return <Num value={row.saves.value} withTooltip={false} />;
      case 'savesX':
        return <MultiplierCell cell={row.saves} />;
      case 'sends':
        return <Num value={row.sends.value} withTooltip={false} />;
      case 'sendsX':
        return <MultiplierCell cell={row.sends} />;
      case 'engagements':
        return <Num value={row.engagements.value} withTooltip={false} />;
      case 'engagementsX':
        return <MultiplierCell cell={row.engagements} />;
      case 'profileViewers':
        return <Num value={row.profileViewers.value} withTooltip={false} />;
      case 'profileViewersX':
        return <MultiplierCell cell={row.profileViewers} />;
      case 'followersGained':
        return <Num value={row.followersGained.value} withTooltip={false} />;
      case 'followersGainedX':
        return <MultiplierCell cell={row.followersGained} />;
      case 'inNetworkPct':
        // No chip: a share is not a count, so there is no median to divide it by.
        return <Num value={row.inNetwork} withTooltip={false} />;
      case 'capturedAt':
        return <span>{row.capturedAtDisplay}</span>;
      // The trailing rate group. No chip: a rate over the row's own impressions has no
      // dataset-wide median to divide it by.
      case 'profileVisitRate':
        return <Num value={row.profileVisitRate} withTooltip={false} />;
      case 'followersRate':
        return <Num value={row.followersRate} withTooltip={false} />;
      case 'reactionsRate':
        return <Num value={row.reactionsRate} withTooltip={false} />;
      case 'commentsRate':
        return <Num value={row.commentsRate} withTooltip={false} />;
      case 'repostsRate':
        return <Num value={row.repostsRate} withTooltip={false} />;
      case 'savesRate':
        return <Num value={row.savesRate} withTooltip={false} />;
      case 'sendsRate':
        return <Num value={row.sendsRate} withTooltip={false} />;
    }
  };
}

/** Sorting is `manualSorting` (§5.4): a header click writes the shared sort and the server orders. */
export function buildContentPostsColumns(
  context: ContentPostsColumnContext,
): ColumnDef<ContentPostRowDTO>[] {
  return CONTENT_POSTS_COLUMNS.map((column) => {
    const Cell = postsCellRenderer(column.id, context);
    return {
      id: column.id,
      header: column.label,
      enableSorting: column.sortable,
      cell: (info) => <Cell row={info.row.original} />,
    } satisfies ColumnDef<ContentPostRowDTO>;
  });
}

/* ========================================================================== */
/*  Comments                                                                  */
/* ========================================================================== */

const COMMENTS_COLUMN_WIDTH: Partial<Record<ContentCommentsColumnId, number>> = {
  // The posts table's flex basis, fixed: this grid has no flex column, so the widest cell states
  // its own width rather than inheriting whatever is left over.
  comment: 340,
  postAuthor: 190,
  commentedAt: 170,
  impressions: 110,
  impressionsX: MULTIPLIER_WIDTH,
  isReply: 90,
  post: 200,
  capturedAt: 170,
};

export function contentCommentsColumnWidth(id: ContentCommentsColumnId): number {
  return COMMENTS_COLUMN_WIDTH[id] ?? 130;
}

function commentsCellRenderer(id: ContentCommentsColumnId) {
  return function Cell({ row }: { row: ContentCommentRowDTO }) {
    switch (id) {
      case 'comment':
        return (
          <span
            className="cell-ellipsis"
            title={row.textTruncated ? `${row.commentText} ${TRUNCATED_NOTE}` : row.commentText}
          >
            {row.previewText}
          </span>
        );
      case 'commentedAt':
        return <span>{row.commentedAtDisplay}</span>;
      case 'impressions':
        return <Num value={row.impressions.value} withTooltip={false} />;
      case 'impressionsX':
        return <MultiplierCell cell={row.impressions} />;
      case 'isReply':
        return row.isReply ? (
          <span className="tag">Reply</span>
        ) : (
          <span className="faint" aria-label="not a reply">
            —
          </span>
        );
      case 'postAuthor': {
        const name = row.postAuthorName ?? '—';
        return row.postAuthorUrl === null ? (
          <span className="cell-ellipsis">{name}</span>
        ) : (
          <OutLink href={row.postAuthorUrl}>
            <span className="cell-ellipsis">{name}</span>
          </OutLink>
        );
      }
      case 'post':
        // The post itself is not in the merged dataset — only the comment is — so this is a link
        // out, titled with when the post went up.
        return row.postUrl === null ? (
          <span className="faint">—</span>
        ) : (
          <OutLink href={row.postUrl}>
            <ExternalLinkIcon size={14} />
            <span className="cn-link__label" title={row.postPublishedAtDisplay}>
              {row.postPublishedAtDisplay}
            </span>
          </OutLink>
        );
      case 'capturedAt':
        return <span>{row.capturedAtDisplay}</span>;
    }
  };
}

export function buildContentCommentsColumns(): ColumnDef<ContentCommentRowDTO>[] {
  return CONTENT_COMMENTS_COLUMNS.map((column) => {
    const Cell = commentsCellRenderer(column.id);
    return {
      id: column.id,
      header: column.label,
      enableSorting: column.sortable,
      cell: (info) => <Cell row={info.row.original} />,
    } satisfies ColumnDef<ContentCommentRowDTO>;
  });
}
